"use client";

import {
  createContext,
  useContext,
  useMemo,
  type ReactNode,
} from "react";
import { detectClientLocale } from "./detect";
import {
  type AppLocale,
  type Terms,
  DEFAULT_LOCALE,
  getTerms,
} from "./terminology";

interface LocaleContextValue {
  locale: AppLocale;
  terms: Terms;
}

const LocaleContext = createContext<LocaleContextValue>({
  locale: DEFAULT_LOCALE,
  terms: getTerms(DEFAULT_LOCALE),
});

/**
 * Provides locale-aware terminology to the component tree.
 * If no explicit locale is passed, auto-detects from the browser.
 */
export function LocaleProvider({
  locale: explicitLocale,
  children,
}: {
  locale?: AppLocale;
  children: ReactNode;
}) {
  const value = useMemo(() => {
    // Allow ?locale=en-GB or ?locale=en-US override via URL (client only)
    if (typeof window !== "undefined") {
      const param = new URLSearchParams(window.location.search).get("locale");
      if (param === "en-GB" || param === "en-US") {
        return { locale: param, terms: getTerms(param) };
      }
    }
    // Use server-detected locale if provided, otherwise detect from browser
    const locale = explicitLocale ?? detectClientLocale();
    return { locale, terms: getTerms(locale) };
  }, [explicitLocale]);

  return (
    <LocaleContext.Provider value={value}>{children}</LocaleContext.Provider>
  );
}

/** Access the current locale and all terminology strings. */
export function useLocale(): LocaleContextValue {
  return useContext(LocaleContext);
}

/** Shorthand: get just the terms object. */
export function useTerms(): Terms {
  return useContext(LocaleContext).terms;
}
